<template>
  <div class="home">
    <section class="py-3">
      <div class="container text-center">
        <div class="row">
          <div class="col-11 mx-auto">
            <img
              id="logo"
              class="img-fluid"
              :src="`${publicPath}images/logo-trinsio.png`"
              alt
            />
          </div>
        </div>
      </div>
    </section>

    <section
      id="top"
      class="py-5 text-white"
      :style="{ backgroundImage: `url('${publicPath}images/top-bg.jpg')` }"
    >
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-4 text-center text-lg-right mb-3 mb-lg-0">
            <img
              class="img-fluid"
              :src="`${publicPath}images/calc-icon.svg`"
              alt
            />
          </div>
          <div class="col-lg-8 text-center text-lg-left">
            <h1 class="text-uppercase">Recovery Time Calculator</h1>
            <h4 class="font-weight-normal">
              Evaluate your Recovery Time and Recovery Point Objectives
            </h4>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-secondary pb-5">
      <div class="container">
        <div class="row">
          <div class="col-lg-6 pt-5">
            <div class="border-bottom mb-4">
              <h3 class="text-primary mb-3">Set Your Recovery Objectives</h3>
              <div class="py-1">
                <p class="mb-5">
                  How many hours can your business go without accessing its
                  critical systems or servers?
                </p>

                <UiRangeInput
                  v-model.number="recoveryCriticalTimeObjective"
                  :min="1"
                  :max="192"
                  :step="1"
                  :markers="[1, 96, 192]"
                />
              </div>

              <div class="py-1">
                <p class="mb-5">
                  How many hours of production time on those servers are you
                  willing to lose?
                </p>
                <div class="form-group mt-3">
                  <UiRangeInput
                    v-model.number="productionRecoveryTimeObjective"
                    :min="1"
                    :max="192"
                    :step="1"
                    :markers="[1, 96, 192]"
                  />
                </div>
              </div>
            </div>

            <div class="border-bottom pb-3 mb-4">
              <h3 class="text-primary mb-3">Recovery Process & Data Storage</h3>

              <div class="py-1">
                <p class="mb-3">
                  How many gigabytes of data are on your critical business
                  systems? Critical systems are the systems essential to your
                  business operations.
                </p>

                <div class="row">
                  <div class="col-lg-6">
                    <UiNumberStepper v-model.number="systemData" label="GB" />
                  </div>
                </div>
              </div>

              <div class="py-1">
                <p class="mb-3">
                  How often do you currently back up these systems, e.g. what is
                  the time between each backup?
                </p>

                <div class="row">
                  <div class="col-lg-6">
                    <UiNumberStepper
                      v-model.number="timeBetweenBackups.hours"
                      label="Hours"
                    />
                  </div>

                  <div class="col-lg-6">
                    <UiNumberStepper
                      v-model.number="timeBetweenBackups.minutes"
                      label="Minutes"
                    />
                  </div>
                </div>
              </div>

              <div class="py-1">
                <p class="mb-3">
                  How long does it take to initiate your recovery process? This
                  is the amount of time that it will take you to prepare the
                  systems for a recovery or restore.
                </p>

                <div class="row">
                  <div class="col-lg-6">
                    <UiNumberStepper
                      v-model.number="timeInitiateBackup.hours"
                      label="Hours"
                    />
                  </div>

                  <div class="col-lg-6">
                    <UiNumberStepper
                      v-model.number="timeInitiateBackup.minutes"
                      label="Minutes"
                    />
                  </div>
                </div>
              </div>

              <div class="py-1">
                <p class="mb-3">
                  Are you recovering data from a local network, or the cloud?
                  This affects the speed at which the data can be recovered.
                </p>

                <div>
                  <UiSwitch
                    v-model="recoveryProcess"
                    label-right="Cloud"
                    label-left="Local"
                  />
                </div>
              </div>
            </div>

            <div class="pb-3 mb-4">
              <h3 class="text-primary mb-3">Downtime & Recovery Costs</h3>

              <div class="py-1">
                <p class="mb-5">
                  How many employees would be affected if your critical systems
                  failed?
                </p>

                <UiRangeInput
                  v-model.number="affectedEmployees"
                  :min="1"
                  :max="1000"
                  :markers="[1, 500, 1000]"
                />
              </div>

              <div class="py-1">
                <p class="mb-5">
                  What is the revenue generated per hour by these employees?
                </p>

                <UiRangeInput
                  v-model.number="hourlyRevenue"
                  :min="0"
                  :max="5000"
                  :markers="['$0', '$2500', '$5000']"
                />
              </div>

              <div class="py-1">
                <p class="mb-5">
                  What is the average hourly wage of an employee using one of
                  your critical systems?
                </p>

                <UiRangeInput
                  v-model.number="averageWage"
                  :min="1"
                  :max="500"
                  :markers="['$1', '$250', '$500']"
                />
              </div>

              <div class="py-1">
                <p class="mb-5">
                  What is the average hourly overhead cost of an employee using
                  a critical system?
                </p>

                <UiRangeInput
                  v-model.number="overheadCost"
                  :min="1"
                  :max="500"
                  :markers="['$1', '$250', '$500']"
                />
              </div>
            </div>
          </div>

          <div class="ml-auto col-lg-5">
            <div class="sticky-top pt-lg-5">
              <div class="bg-white shadow-sm px-4 py-4 rounded">
                <h3 class="text-primary">Calculate your recovery cost</h3>

                <div class="py-3">
                  <h1 class="result mb-0 text-primary">
                    {{ totalCost | currency }}
                  </h1>
                  <h5 class="text-uppercase mb-4 text-light m-0">
                    Total Cost To Business
                  </h5>
                </div>

                <div
                  class="pb-3 mb-3"
                  style="border-bottom: 1px solid rgba(0,0,0,0.25) !important;"
                >
                  <h4 class="text-uppercase">Recovery Process</h4>

                  <div class="row">
                    <div class="col-md-6">
                      <div class="py-2">
                        <div class="d-flex">
                          <h4 class="mr-3">
                            <i class="fas fa-server"></i>
                          </h4>
                          <div class="data">
                            <h5 class="font-weight-bold m-0">
                              {{ systemData }} GB
                            </h5>
                            <p class="m-0">
                              <small>Critical Systems Data</small>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="py-2">
                        <div class="d-flex">
                          <h4 class="mr-3">
                            <i class="fas fa-clock"></i>
                          </h4>
                          <div class="data">
                            <h5 class="font-weight-bold m-0">
                              {{ totalTimeBetweenBackups | timeFormat }}
                            </h5>
                            <p class="m-0">
                              <small>Time between backups</small>
                            </p>

                            <p
                              class="notification font-weight-bold text-danger"
                              v-if="hasRTOexceeded"
                            >
                              RTO Exceeded
                            </p>
                            <p
                              class="notification font-weight-bold text-success"
                              v-else
                            >
                              RTO Met
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="py-2">
                        <div class="d-flex">
                          <h4 class="mr-3">
                            <i class="fas fa-clock"></i>
                          </h4>
                          <div class="data">
                            <h5 class="font-weight-bold m-0">
                              {{ totalInitiateBackup | timeFormat }}
                            </h5>
                            <p class="m-0">
                              <small>Recovery Process Initiation</small>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="py-2">
                        <div class="d-flex">
                          <h4 class="mr-3">
                            <i class="fas fa-clock"></i>
                          </h4>
                          <div class="data">
                            <h5 class="font-weight-bold m-0">
                              {{ estimatedDowntime | timeFormat }}
                            </h5>
                            <p class="m-0">
                              <small>Estimated Downtime</small>
                            </p>
                            <p
                              class="notification font-weight-bold text-danger"
                              v-if="hasRPOexceeded"
                            >
                              RPO Exceeded
                            </p>
                            <p
                              class="notification font-weight-bold text-success"
                              v-else
                            >
                              RPO Met
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="pb-3 mb-3">
                  <h4 class="text-uppercase">Downtime Costs</h4>

                  <div class="row">
                    <div class="col-md-6">
                      <div class="py-2">
                        <div class="d-flex">
                          <h4 class="mr-3">
                            <i class="fas fa-users"></i>
                          </h4>
                          <div class="data">
                            <h5 class="font-weight-bold m-0">
                              {{ affectedEmployees }}
                            </h5>
                            <p class="m-0">
                              <small>Employees Affected</small>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="py-2">
                        <div class="d-flex">
                          <h4 class="mr-3">
                            <i class="fas fa-hand-holding-usd"></i>
                          </h4>
                          <div class="data">
                            <h5 class="font-weight-bold m-0">
                              {{ averageWage | currency }}
                            </h5>
                            <p class="m-0">
                              <small>Average Employee Wage</small>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="py-2">
                        <div class="d-flex">
                          <h4 class="mr-3">
                            <i class="fas fa-hand-holding-usd"></i>
                          </h4>
                          <div class="data">
                            <h5 class="font-weight-bold m-0">
                              {{ overheadCost | currency }}
                            </h5>
                            <p class="m-0">
                              <small>Average Employee Overhead Cost</small>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="py-2">
                        <div class="d-flex">
                          <h4 class="mr-3">
                            <i class="fas fa-hand-holding-usd"></i>
                          </h4>
                          <div class="data">
                            <h5 class="font-weight-bold m-0">
                              {{ hourlyRevenueCost | currency }}
                            </h5>
                            <p class="m-0">
                              <small>Hourly Revenue Cost</small>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <a
                  href="#form"
                  class="btn btn-block btn-primary font-weight-bold"
                  >Protect Me</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <QuotationForm :quote="getQuoteSummary" />

    <footer class="bg-secondary py-5">
      <div class="container">
        <p class="font-weight-bold">DISCLAIMER:</p>
        <p>
          Trinsio is not responsible for any actions taken, errors or omissions
          (negligent or otherwise), regardless of the cause, or for the results
          obtained from the use of this content, or for the performance of any
          computer, hardware or software used or modified in conjunction with
          this content. The content is provided on an “as is” basis.
        </p>
        <p>
          VIEWERS OF THIS REPORT AND ITS CONTENT DISCLAIM ANY AND ALL EXPRESS OR
          IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, ANY WARRANTIES OF
          MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE OR USE, FREEDOM
          FROM BUGS, SOFTWARE ERRORS OR DEFECTS, THAT THE CONTENT’S FUNCTIONING
          WILL BE UNINTERRUPTED OR THAT THE CONTENT WILL OPERATE WITH ANY
          SOFTWARE OR HARDWARE CONFIGURATION.
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
import QuotationForm from "@/views/QuotationForm.vue";
import * as Config from "@/config/recovery.json";

export default {
  name: "home",
  components: {
    QuotationForm
  },

  filters: {
    currency: function(amount) {
      return amount
        ? ((amount = amount.toString()),
          "$" + amount.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,"))
        : "";
    },
    timeFormat: function(time) {
      return (
        (time = Math.floor(time)),
        Math.floor(time / 86400) +
          " DAYS " +
          (Math.floor(time / 3600) % 24) +
          " HRS " +
          `${Math.floor(time / 60) % 60}`.padStart(2, "0") +
          " MINS "
      );
    }
  },

  computed: {
    hasRTOexceeded() {
      return (
        this.totalTimeBetweenBackups >
        this.productionRecoveryTimeObjective * 3600
      );
    },

    hasRPOexceeded() {
      return this.estimatedDowntime > this.recoveryCriticalTimeObjective * 3600;
    },

    /**
     * Recovery speed has 2 types:
     * Local and Cloud
     * Local: 30720
     * Cloud: 716800
     *
     * @return {Number}
     */
    recoverySpeedTime() {
      return this.recoveryProcess
        ? Config.recoverySpeedTime.local
        : Config.recoverySpeedTime.cloud;
    },

    totalTimeBetweenBackups() {
      let { hours, minutes } = this.timeBetweenBackups;
      return hours * 3600 + minutes * 60;
    },

    totalInitiateBackup() {
      let { hours, minutes } = this.timeInitiateBackup;
      return hours * 3600 + minutes * 60;
    },

    hourlyRevenueCost: function() {
      return (
        this.affectedEmployees * this.averageWage +
        this.overheadCost +
        this.hourlyRevenue
      );
    },

    estimatedDowntime: function() {
      return (
        (Config.multiplier.downtimePerGigabyte * this.systemData) /
          this.recoverySpeedTime +
        this.totalInitiateBackup
      );
    },

    totalCost: function() {
      return Math.floor(
        this.hourlyRevenueCost * (this.estimatedDowntime / 3600)
      );
    },

    getQuoteSummary() {
      const {
        // Objectives
        recoveryCriticalTimeObjective,
        productionRecoveryTimeObjective,

        systemData,
        timeBetweenBackups,
        hasRTOexceeded,
        recoveryProcess,

        timeInitiateBackup,
        estimatedDowntime,
        hasRPOexceeded,

        affectedEmployees,
        averageWage,
        overheadCost,
        hourlyRevenueCost,

        recoverySpeedTime,
        totalTimeBetweenBackups,
        totalInitiateBackup,
        totalCost
      } = this;
      const { timeFormat } = this.$options.filters;

      return {
        systemData,
        timeBetweenBackups,
        hasRTOexceeded,

        timeInitiateBackup,
        estimatedDowntime,
        estimatedDowntimeText: timeFormat(estimatedDowntime),
        hasRPOexceeded,

        affectedEmployees,
        averageWage,
        overheadCost,
        hourlyRevenueCost,
        recoveryProcess, // if false local : cloud,
        recoveryProcessType: recoveryProcess ? "cloud" : "local",
        productionRecoveryTimeObjective,
        recoveryCriticalTimeObjective,
        recoverySpeedTime,
        totalTimeBetweenBackups,
        totalTimeBetweenBackupsText: timeFormat(totalTimeBetweenBackups),

        totalInitiateBackup,
        totalInitiateBackupText: timeFormat(totalInitiateBackup),
        totalCost
      };
    }
  },

  data() {
    return {
      ...Config.defaults,
      publicPath: process.env.BASE_URL
    };
  }
};
</script>
